import { EMPTY_VALUE } from '@/constants/app.constants';
import { cn } from '@/lib/utils';
import { formatNumber } from '@/utils/number';
import type { PcmpMisStatusLine, PcmpMisStatusReport } from '../../types/pcmpMisStatusReport.types';

const cellClass = 'border border-neutral-300 px-2 py-1';
const headClass = cn(cellClass, 'bg-neutral-100 text-center font-semibold text-neutral-900');
const numClass = 'text-right tabular-nums whitespace-nowrap';

function Figures({
  line,
  reasons,
  bold,
}: {
  line: PcmpMisStatusLine;
  reasons: PcmpMisStatusReport['reasons'];
  bold?: boolean;
}) {
  return (
    <>
      <td className={cn(cellClass, numClass, bold && 'font-bold')}>
        {formatNumber(line.verified)}
      </td>
      {reasons.map((reason) => (
        <td key={reason.code} className={cn(cellClass, numClass, bold && 'font-bold')}>
          {formatNumber(line.reasons[reason.code])}
        </td>
      ))}
      <td className={cn(cellClass, numClass, 'font-semibold', bold && 'font-bold')}>
        {formatNumber(line.notVerified)}
      </td>
      <td className={cn(cellClass, numClass, 'font-semibold', bold && 'font-bold')}>
        {formatNumber(line.total)}
      </td>
    </>
  );
}

/** MIS decisions per team member: Verified, a column per Not Verified reason, then the totals. */
export function PcmpMisStatusReportTable({
  report,
  className,
}: {
  report: PcmpMisStatusReport;
  className?: string;
}) {
  const { reasons, lines, total } = report;

  return (
    <table className={cn('w-full border-collapse text-xs', className)}>
      <thead>
        <tr>
          <th rowSpan={2} className={headClass}>
            MIS Member
          </th>
          <th rowSpan={2} className={headClass}>
            HR ID
          </th>
          <th rowSpan={2} className={headClass}>
            Verified
          </th>
          <th colSpan={reasons.length} className={headClass}>
            Not Verified, by reason
          </th>
          <th rowSpan={2} className={headClass}>
            Not Verified
          </th>
          <th rowSpan={2} className={headClass}>
            Total Updates
          </th>
        </tr>
        <tr>
          {reasons.map((reason) => (
            <th key={reason.code} className={headClass}>
              {reason.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {lines.map((line) => (
          <tr key={line.member.userId}>
            <td className={cn(cellClass, 'whitespace-nowrap')}>{line.member.name}</td>
            <td className={cn(cellClass, 'whitespace-nowrap')}>
              {line.member.hrId ?? EMPTY_VALUE}
            </td>
            <Figures line={line} reasons={reasons} />
          </tr>
        ))}
        <tr className="bg-neutral-200 font-bold">
          <td colSpan={2} className={cellClass}>
            Total
          </td>
          <Figures line={total} reasons={reasons} bold />
        </tr>
      </tbody>
    </table>
  );
}
